import axios from 'axios'
import CryptoJS from 'crypto-js'

const baseURL = '/api'

let __kickPromptActive = false

class Request {
  constructor () {
    // 默认加密配置
    this.cryptoConfig = {
      mode: 'ECB', // 使用ECB模式
      pad: 'Pkcs7', // 使用PKCS7填充
      key: '$e$Pp7h80F5k4D3s', // 密钥初始为空，将从app.vue设置
      keyType: 'Utf8', // 密钥编码类型
      iv: '', // ECB模式不需要IV
      ivType: 'Utf8', // IV编码类型
      isBase64: true // 使用Base64输出
    }

    // 存储Vue实例引用
    this.vueInstance = null
  }

  // 设置Vue实例
  setVueInstance (vueInstance) {
    this.vueInstance = vueInstance
    // 从Vue实例中获取密钥
    if (vueInstance && vueInstance.base) {
      this.cryptoConfig.key = vueInstance.base
    }
  }

  // AES加密方法
  encryptData (data) {
    // 如果没有密钥则不加密
    if (!this.cryptoConfig.key) return data

    const { mode, pad, key, keyType, iv, ivType, isBase64 } = this.cryptoConfig

    try {
      const cryptoKey = CryptoJS.enc[keyType].parse(key)
      const cfg = {
        mode: CryptoJS.mode[mode],
        padding: CryptoJS.pad[pad]
      }

      if (mode !== 'ECB' && iv) {
        cfg.iv = CryptoJS.enc[ivType].parse(iv)
      }

      // 如果数据是对象，先转为JSON字符串
      const dataStr = typeof data === 'object' ? JSON.stringify(data) : data

      const encrypted = CryptoJS.AES.encrypt(dataStr, cryptoKey, cfg)
      return encrypted.ciphertext.toString(isBase64 ? CryptoJS.enc.Base64 : CryptoJS.enc.Hex)
    } catch (error) {
      console.error('加密失败:', error)
      return data
    }
  }

  // AES解密方法
  decryptData (encryptedData) {
    // 如果没有密钥则不解密
    if (!this.cryptoConfig.key) return encryptedData

    const { mode, pad, key, keyType, iv, ivType, isBase64 } = this.cryptoConfig

    try {
      const cryptoKey = CryptoJS.enc[keyType].parse(key)
      const cfg = {
        mode: CryptoJS.mode[mode],
        padding: CryptoJS.pad[pad]
      }

      if (mode !== 'ECB' && iv) {
        cfg.iv = CryptoJS.enc[ivType].parse(iv)
      }

      // 处理输入数据
      const cryptoData = isBase64
        ? encryptedData
        : CryptoJS.enc.Base64.stringify(CryptoJS.enc.Hex.parse(encryptedData))

      const decrypt = CryptoJS.AES.decrypt(cryptoData, cryptoKey, cfg)
      const result = CryptoJS.enc.Utf8.stringify(decrypt)

      // 尝试解析为JSON对象，如果不是JSON则返回原始字符串
      try {
        return JSON.parse(result)
      } catch {
        return result
      }
    } catch (error) {
      console.error('解密失败:', error)
      return encryptedData
    }
  }

  request (url, method, data, headers) {
    if (method === 'post') {
      headers = {
        'Content-Type': 'application/json;charset=UTF-8',
        ...headers
      }
    }

    // 在发送前加密数据
    let processedData = data
    if (this.cryptoConfig.key && method === 'post') {
      processedData = this.encryptData(data)
    }

    // 允许通过 headers 参数传入 axios 其他配置（如 timeout）
    const { timeout, ...restHeaders } = headers || {}

    return new Promise((resolve, reject) => {
      const fullUrl = `${baseURL}/${url}`
      // eslint-disable-next-line no-empty
      try {
      } catch (e) {}
      axios({
        url: fullUrl,
        method,
        params: method === 'get' ? data : {},
        data: method === 'post' ? JSON.stringify({ reqData: processedData }) : {},
        headers: restHeaders,
        timeout
      }).then(res => {
        if (res.status === 200) {
          // 解密逻辑增强
          let decryptedData = res.data
          if (this.cryptoConfig.key) {
            // 如果data是加密字符串，直接解密
            if (typeof res.data === 'string') {
              decryptedData = this.decryptData(res.data)
              try {
                decryptedData = JSON.parse(decryptedData) // 尝试解析为JSON
              } catch (e) {
                // 如果不是JSON，保持原样
              }
              // eslint-disable-next-line brace-style
            }
            // 如果data是对象且包含加密字段
            else if (res.data.data && typeof res.data.data === 'string') {
              // 尝试解密，但如果解密后为空或无效，保持原始值
              const originalDataValue = res.data.data
              const decrypted = this.decryptData(originalDataValue)
              // 如果解密后为空字符串、null、undefined 或与原始值相同（说明可能不是加密的），保持原始值
              if (!decrypted || decrypted === '' || (typeof decrypted === 'string' && decrypted === originalDataValue)) {
                // 保持原始 data，不进行解密
                decryptedData = res.data
              } else {
                try {
                  // 尝试解析为JSON，如果失败则保持为字符串
                  const parsed = JSON.parse(decrypted)
                  decryptedData = {
                    ...res.data,
                    data: parsed
                  }
                } catch (e) {
                  // 如果解密后不是JSON（可能是普通字符串），保持原样
                  decryptedData = {
                    ...res.data,
                    data: decrypted
                  }
                }
              }
            }
          }
          // 全局检测：账号在别处登录
          try {
            const payload = decryptedData
            if (!payload || typeof payload !== 'object') return

            const topLevelCode = payload.code
            const topLevelMsg = payload.message
            const inner = payload.data

            // 检测内层 data 字段
            let kickedByInner = false
            let kickedByStr = false
            let tokenInvalid = false
            let loginExpired = false

            if (inner) {
              // 如果 inner 是对象，检查 code 和 message
              if (typeof inner === 'object' && inner !== null) {
                const innerCode = inner.code
                const innerMsg = inner.message
                // 检测"已被顶下线"
                kickedByInner = (innerCode === 500 && typeof innerMsg === 'string' && innerMsg.indexOf('已被顶下线') !== -1)
                // 检测"token无效"
                if (!tokenInvalid && typeof innerMsg === 'string' && innerMsg.indexOf('token 无效') !== -1) {
                  tokenInvalid = true
                }
                // 检测"登录信息已过期"
                if (!loginExpired && innerCode === 500 && typeof innerMsg === 'string' && innerMsg.indexOf('登录信息已过期') !== -1) {
                  loginExpired = true
                }
                // eslint-disable-next-line brace-style
              }
              // 如果 inner 是字符串，检查是否包含"已在另一台设备上登录"或"token无效"
              else if (typeof inner === 'string') {
                kickedByStr = inner.indexOf('已在另一台设备上登录') !== -1
                if (!tokenInvalid && inner.indexOf('token 无效') !== -1) {
                  tokenInvalid = true
                }
                if (!loginExpired && inner.indexOf('登录信息已过期') !== -1) {
                  loginExpired = true
                }
              }
            }

            // 检测顶层 message
            const kickedByTop = (topLevelCode === 500 && typeof topLevelMsg === 'string' && topLevelMsg.indexOf('已被顶下线') !== -1)
            // 检测顶层 message 中的"token无效"
            if (!tokenInvalid && typeof topLevelMsg === 'string' && topLevelMsg.indexOf('token 无效') !== -1) {
              tokenInvalid = true
            }
            // 检测顶层 message 中的"登录信息已过期"
            if (!loginExpired && topLevelCode === 500 && typeof topLevelMsg === 'string' && topLevelMsg.indexOf('登录信息已过期') !== -1) {
              loginExpired = true
            }

            // 如果检测到被顶下线，显示提示并弹出登录弹窗
            if (kickedByInner || kickedByStr || kickedByTop) {
              this.handleTokenKicked(true)
              // eslint-disable-next-line brace-style
            }
            // 如果检测到登录信息已过期，显示提示并弹出登录弹窗
            else if (loginExpired) {
              this.handleTokenKicked(true, '登录信息已过期，请重新登录')
              // eslint-disable-next-line brace-style
            }
            // 如果检测到 token 无效，只弹出登录弹窗，不显示提示
            else if (tokenInvalid) {
              this.handleTokenKicked(false)
            }
          } catch (e) {
            // 忽略检测错误，不影响正常响应
            try { console.warn('[token-kick] detection error:', e) } catch (e2) {}
          }
          resolve(decryptedData)
        }
      }).catch(error => {
        try { console.warn('[http] request failed', { method, url: fullUrl, error }) } catch (e) {}
        reject(error)
      })
    })
  }

  handleTokenKicked (showMessage = true, customMessage) {
    try {
      if (__kickPromptActive) return
      __kickPromptActive = true
      // 清除本地登录标记
      try {
        window.localStorage.removeItem('userInfo')
        window.localStorage.setItem('isLogin', 'false')
      } catch (e) {}

      // 如果需要显示提示，则显示提示弹窗
      if (showMessage) {
        const vm = this.vueInstance
        const message = customMessage || '您的账户已在别处登录，请重新登录'

        // 优先使用 Element UI 的 alert
        if (vm && vm.$alert) {
          vm.$alert(message, '提示', {
            confirmButtonText: '重新登录',
            customClass: 'messagebox-themed',
            callback: () => {
              try {
                window.dispatchEvent(new CustomEvent('force-login'))
              } catch (e) {}
            }
          })
        } else {
          // 降级处理：使用原生 alert，然后触发登录事件
          try {
            alert(message)
          } catch (e) {}
          // 延迟触发，确保 alert 关闭后再显示登录弹窗
          setTimeout(() => {
            try {
              window.dispatchEvent(new CustomEvent('force-login'))
            } catch (e) {}
          }, 100)
        }
      } else {
        // 不显示提示，直接触发登录弹窗
        try {
          window.dispatchEvent(new CustomEvent('force-login'))
        } catch (e) {}
      }
    } catch (e) {
      // 即使出错也要触发登录事件
      try {
        window.dispatchEvent(new CustomEvent('force-login'))
      } catch (e2) {}
    }
  }

  get (url, data = {}, headers = {}) {
    return this.request(url, 'get', data, headers)
  }

  post (url, data = {}, headers = {}) {
    return this.request(url, 'post', data, headers)
  }
}

const requestInstance = new Request()
export default requestInstance
